// Pure chart arithmetic for the home screen. Input is a normalized series (lib/ops-matrix.js normalizeSeries).
// Nothing here invents a day or a count: an empty selection stays empty.
import { isUnproven } from '@/lib/ops-matrix.js'

export const W = 720
export const H = 240
export const PAD = { left: 44, right: 40, top: 14, bottom: 26 }
export const PLOT = { x0: PAD.left, x1: W - PAD.right, y0: PAD.top, y1: H - PAD.bottom }

export const niceMax = n => {
  if (!(n > 0)) return 1
  const p = 10 ** Math.floor(Math.log10(n))
  const f = n / p
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * p
}

export const xAt = (i, n) => (n <= 1 ? (PLOT.x0 + PLOT.x1) / 2 : PLOT.x0 + ((PLOT.x1 - PLOT.x0) * i) / (n - 1))
export const yAt = (v, max) => PLOT.y1 - ((PLOT.y1 - PLOT.y0) * v) / max

// Which day the pointer is over, from its fraction of the chart width. Null outside the plot.
export function dayIndexAt(fraction, n) {
  if (!(n > 0) || !Number.isFinite(fraction)) return null
  const x = fraction * W
  if (x < PLOT.x0 - 8 || x > PLOT.x1 + 8) return null
  if (n === 1) return 0
  const i = Math.round(((x - PLOT.x0) / (PLOT.x1 - PLOT.x0)) * (n - 1))
  return Math.min(n - 1, Math.max(0, i))
}

const nums = list => list.filter(v => v !== null)
const total = list => (list.some(v => v === null) ? null : list.reduce((t, v) => t + v, 0))

// A campaign on a day is proven (a count), a gap (the ledger has no row) or not proven (sends whose outcome is unknown).
// A not proven day carries the sends already confirmed that day, but its sent value is null: it is never drawn as a
// level, never a zero and never part of a total. Unknown counts are never added across days.
const sentOf = (day, id) => (isUnproven(day, id) ? null : day.sent[id] ?? null)

// One campaign: sent and replies per day. A day with no count is a gap (null), never a zero, and a total over a gap is null.
export function singleSeries(series, id) {
  const days = series.days.map(d => ({ date: d.date, sent: sentOf(d, id), confirmed: d.sent[id] ?? null, unproven: isUnproven(d, id), replies: d.replies[id] ?? null, held: series.held?.[d.date] ?? 0 }))
  const maxS = niceMax(Math.max(0, ...nums(days.map(d => d.sent))))
  const maxR = niceMax(Math.max(0, ...nums(days.map(d => d.replies))))
  const pts = (key, max) => days.map((d, i) => (d[key] === null ? null : [xAt(i, days.length), yAt(d[key], max)]))
  const unprovenDays = days.filter(d => d.unproven).length
  return { days, maxS, maxR, sent: pts('sent', maxS), replies: pts('replies', maxR), totalSent: total(days.map(d => d.sent)), totalReplies: total(days.map(d => d.replies)), unprovenDays }
}

// Every campaign that is not hidden, stacked in list order. A layer with no count is drawn with no height and its
// day total, and the totals that include it, are null. A not proven layer is the same, and the day says why.
export function stackedSeries(series, ids, hidden) {
  const visible = ids.filter(id => !hidden.has(id))
  const days = series.days.map(d => {
    let base = 0
    const layers = visible.map(id => { const v = sentOf(d, id); const layer = { id, value: v, confirmed: d.sent[id] ?? null, unproven: isUnproven(d, id), from: base, to: base + (v ?? 0) }; base += v ?? 0; return layer })
    return { date: d.date, layers, drawn: base, total: total(layers.map(l => l.value)), unproven: layers.some(l => l.unproven), replies: total(visible.map(id => d.replies[id] ?? null)), held: series.held?.[d.date] ?? 0 }
  })
  const max = niceMax(Math.max(0, ...days.map(d => d.drawn)))
  return { days, visible, max, totalSent: total(days.map(d => d.total)), totalReplies: total(days.map(d => d.replies)), unprovenDays: days.filter(d => d.unproven).length }
}

export const segments = pts => pts.reduce((acc, p) => {
  if (p === null) acc.push([])
  else { if (!acc.length) acc.push([]); acc[acc.length - 1].push(p) }
  return acc
}, []).filter(seg => seg.length)
const trace = seg => seg.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')
// Points that stand alone (no neighbour on either side): a path of one point draws nothing, so each gets a dot.
export const isolated = pts => segments(pts).filter(seg => seg.length === 1).map(seg => seg[0])
export const line = pts => segments(pts).map(trace).join(' ')
export const areaPath = pts => segments(pts).map(seg => `${trace(seg)} L${seg[seg.length - 1][0].toFixed(1)} ${PLOT.y1} L${seg[0][0].toFixed(1)} ${PLOT.y1} Z`).join(' ')

// Runs of consecutive days on which a layer has a count. A day with no count ends the run, so a gap is never drawn as a zero.
function runsOf(stack, id) {
  const n = stack.days.length
  const runs = []
  stack.days.forEach((d, i) => {
    const l = d.layers.find(x => x.id === id)
    if (l.value === null) { runs.push(null); return }
    if (runs.length === 0 || runs[runs.length - 1] === null) runs.push([])
    runs[runs.length - 1].push({ x: xAt(i, n), from: yAt(l.from, stack.max), to: yAt(l.to, stack.max) })
  })
  return runs.filter(r => r && r.length)
}
const pt = (x, y) => `${x.toFixed(1)} ${y.toFixed(1)}`

// The closed outline of each run of one stacked layer. A run of one day has no width, so it is drawn by bandTicks.
export function bandPath(stack, id) {
  return runsOf(stack, id).filter(r => r.length > 1).map(r => `M${r.map(p => pt(p.x, p.to)).join(' L')} L${[...r].reverse().map(p => pt(p.x, p.from)).join(' L')} Z`).join(' ')
}
export function bandTicks(stack, id) {
  return runsOf(stack, id).filter(r => r.length === 1).map(([p]) => `M${pt(p.x, p.to)} L${pt(p.x, p.from)}`).join(' ')
}
// The x of every day whose total is unknown because a visible layer has no count (a day that is not proven has its own marker).
export const gapColumns = stack => stack.days.map((d, i) => (d.total === null && !d.unproven ? xAt(i, stack.days.length) : null)).filter(x => x !== null)
// The x of every day on which a visible layer has sends whose outcome is unknown.
export const unprovenColumns = stack => stack.days.map((d, i) => (d.unproven ? xAt(i, stack.days.length) : null)).filter(x => x !== null)

// Funnel column heights on a square root scale, so a small stage stays visible next to a large one.
export function funnelHeights(values, maxPx) {
  const top = Math.max(0, ...values.filter(v => Number.isFinite(v)))
  return values.map(v => (Number.isFinite(v) && top > 0 ? (v > 0 ? Math.max(3, Math.round(maxPx * Math.sqrt(v / top))) : 0) : null))
}

export const shortDate = iso => {
  const [, m, d] = iso.split('-')
  return `${Number(m)}/${Number(d)}`
}
export const longDate = iso => {
  const [y, m, d] = iso.split('-')
  return `${m}/${d}/${y}`
}

// The cover sparkline: confirmed sends per day on a 200 by 30 canvas. A gap breaks the line, and a day that is not
// proven is a tick of its own and never a level.
export const SPARK = { w: 200, h: 30, top: 3, base: 27 }
export function sparkShape(days) {
  const n = days.length
  const max = Math.max(0, ...days.map(d => d.value ?? 0))
  const x = i => (n <= 1 ? SPARK.w / 2 : (SPARK.w * i) / (n - 1))
  const y = v => SPARK.base - ((SPARK.base - SPARK.top) * (max > 0 ? v / max : 0))
  const runs = days.reduce((acc, d, i) => {
    if (d.value === null) acc.push([])
    else { if (!acc.length) acc.push([]); acc[acc.length - 1].push([x(i), y(d.value)]) }
    return acc
  }, []).filter(r => r.length)
  return {
    path: runs.filter(r => r.length > 1).map(r => r.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`).join(' ')).join(' '),
    dots: runs.filter(r => r.length === 1).map(r => r[0]),
    unproven: days.map((d, i) => (d.unproven ? x(i) : null)).filter(v => v !== null),
    drawn: days.some(d => d.value !== null),
  }
}

export const percent = rate => `${(rate * 100).toFixed(1)}%`
